/**
 * ImageCell — renders a `type: "image"` column from a URL template.
 *
 * The template lives in the contract (`ScreenerColumn.imageUrl`) rather than the
 * renderer, so a logo column is one declaration that replays on any platform,
 * and the third-party host is visible to anyone reading the workspace.
 *
 * Only the rows AG Grid has virtualised into the DOM mount, so a 500-row screen
 * fetches roughly a screenful of images rather than 500.
 */

import { useEffect, useState } from "react";
import type { ScreenerRow, ScreenerValue } from "@qufolio/transport";

/** The row fields a template reads — an image column's equivalent of `requires`. */
export function imageFields(template: string | undefined): string[] {
  if (!template) return [];
  return [...template.matchAll(/\{(\w+)\}/g)].map((m) => m[1]!);
}

/**
 * Fill `{field}` placeholders from a row.
 *
 * A placeholder whose field is missing or empty yields `undefined` rather than a
 * URL with a hole in it — requesting `.../image-stock/.png` would just be a 404
 * per row.
 */
export function resolveImageUrl(
  template: string,
  row: ScreenerRow | undefined,
): string | undefined {
  if (!row) return undefined;
  let missing = false;

  const url = template.replace(/\{(\w+)\}/g, (_match, field: string) => {
    const value: ScreenerValue | undefined = row[field];
    if (value == null || value === "") {
      missing = true;
      return "";
    }
    return encodeURIComponent(String(value));
  });

  return missing ? undefined : url;
}

export interface ImageCellProps {
  value: unknown;
  data?: ScreenerRow;
  /** From `ScreenerColumn.imageUrl`. */
  template?: string;
  /**
   * Share of the cell the logo occupies, 0–1.
   *
   * Under 1 so the logo reads as a mark inside its row rather than filling the
   * cell edge to edge — at 1 it crowds the adjacent column and grows with row
   * height without limit.
   */
  scale?: number;
}

export function ImageCell({ data, template, scale = 0.75 }: ImageCellProps) {
  const url = template ? resolveImageUrl(template, data) : undefined;
  const [failed, setFailed] = useState(false);

  // A row is recycled as the grid scrolls, so a previous row's failure must not
  // suppress this one's image.
  useEffect(() => setFailed(false), [url]);

  // Deliberately blank rather than a broken-image glyph: a missing logo is
  // unremarkable, and a column of broken icons reads as a bug.
  if (!url || failed) return null;

  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      style={{
        // Sized as a share of the cell rather than a fixed box, so the logo
        // tracks the row height and whatever width the column is dragged to — a
        // fixed box is either lost in a wide column or clipped in a narrow one.
        maxWidth: `${scale * 100}%`,
        maxHeight: `${scale * 100}%`,
        objectFit: "contain",
        borderRadius: 3,
        display: "block",
      }}
    />
  );
}
